import { Form, Col, FormInstance, Input } from "antd";
import { DatePicker } from "antd";
import {
  activeSpecificDate,
  boundsFromEventWindow,
  boundsFromSavedEventSchedule,
  createDateTimeRangeValidator,
  getDisabledDateWithBounds,
  MIN_DATETIME_GAP_MINUTES,
  type DateTimeRangeBounds,
  type SavedEventSchedule,
} from "../utils/DateFn";
import CustomTimePicker from "./CustomTimePicker";
import type { Dayjs } from "dayjs";
import dayjs from "../utils/dayjsConfig";

export type DateTimePickerProps = {
  form: FormInstance;
  label: string[];
  name: string[];
  /** Max calendar day (e.g. event end date for registration) */
  disabledBasedOn?: string;
  betweenDates?: string[];
  disable?: boolean;
  minGapMinutes?: number;
  /** Restrict start/end datetimes to event (or coupon) window */
  eventBounds?: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
  };
  bounds?: DateTimeRangeBounds;
  /** When editing an event, calendar min date = last saved start date (not only today). */
  savedSchedule?: SavedEventSchedule;
  maxDateTimeErrorMessage?: string | ((max: Dayjs) => string);
  /** Lock date fields only (times stay editable), e.g. single-day coupon. */
  disableDatePicker?: boolean;
};

const DateTimePicker = ({
  form,
  label,
  name,
  disabledBasedOn,
  betweenDates,
  disable,
  minGapMinutes = MIN_DATETIME_GAP_MINUTES,
  eventBounds,
  bounds: boundsProp,
  savedSchedule,
  maxDateTimeErrorMessage,
  disableDatePicker = false,
}: DateTimePickerProps) => {
  const hasEnd = Boolean(label[2] && name[2]);
  const editBounds = boundsFromSavedEventSchedule(savedSchedule);
  const rangeBounds: DateTimeRangeBounds =
    boundsProp ??
    editBounds ??
    (eventBounds
      ? boundsFromEventWindow(
          eventBounds.startDate,
          eventBounds.startTime,
          eventBounds.endDate,
          eventBounds.endTime,
        )
      : { minDateTime: null, maxDateTime: null });

  const isScheduleEdit = Boolean(editBounds?.isEdit || rangeBounds.isEdit);
  const datePickerDisabled = Boolean(disable || disableDatePicker);

  const startDateDisabled = (current: Dayjs | null) => {
    if (!current) return false;
    if (betweenDates?.length === 2) {
      return activeSpecificDate(
        dayjs(betweenDates[0]),
        dayjs(betweenDates[1]),
      )(current);
    }
    const maxDay =
      disabledBasedOn ??
      (rangeBounds.maxDateTime
        ? dayjs(rangeBounds.maxDateTime).format("YYYY-MM-DD")
        : undefined);

    return getDisabledDateWithBounds({
      minDate: isScheduleEdit ? savedSchedule?.start_date : undefined,
      maxDate: maxDay,
      floorAtToday: !isScheduleEdit,
    })(current);
  };

  const endDateDisabled = (current: Dayjs | null) => {
    if (!current) return false;
    const startVal = form.getFieldValue(name[0]);
    const startDay = startVal ? dayjs(startVal).startOf("day") : null;

    if (betweenDates?.length === 2) {
      const isOutsideRange = activeSpecificDate(
        dayjs(betweenDates[0]),
        dayjs(betweenDates[1]),
      )(current);
      if (isOutsideRange) return true;
    }

    const maxDay =
      disabledBasedOn ??
      (rangeBounds.maxDateTime
        ? dayjs(rangeBounds.maxDateTime).format("YYYY-MM-DD")
        : undefined);

    const endMinDay =
      startDay ??
      (isScheduleEdit && savedSchedule?.start_date
        ? dayjs(savedSchedule.start_date).startOf("day")
        : null);

    const baseDisabled = getDisabledDateWithBounds({
      minDate: endMinDay ?? undefined,
      maxDate: maxDay,
      floorAtToday: !isScheduleEdit,
    });

    return baseDisabled(current);
  };

  const validatorFields = {
    startDate: name[0],
    startTime: name[1],
    ...(hasEnd ? { endDate: name[2], endTime: name[3] } : {}),
  };

  return (
    <>
      <Form.Item
        name={`_datetime_range_validator_${name.join("_")}`}
        hidden
        dependencies={[
          name[0],
          name[1],
          ...(hasEnd ? [name[2]!, name[3]!] : []),
        ]}
        rules={[
          {
            validator: createDateTimeRangeValidator(form, validatorFields, {
              minGapMinutes,
              bounds: rangeBounds,
              requireEnd: hasEnd,
              maxDateTimeErrorMessage,
            }),
          },
        ]}
      >
        <Input type="hidden" />
      </Form.Item>

      <Col xs={24} sm={12}>
        <Form.Item
          name={name[0]}
          label={label[0]}
          rules={[
            {
              required: true,
              message: "Please select start date",
            },
          ]}
        >
          <DatePicker
            disabled={datePickerDisabled}
            disabledDate={startDateDisabled}
            style={{ width: "100%" }}
            onChange={(date: Dayjs | null) => {
              if (date && hasEnd) {
                const endVal = form.getFieldValue(name[2]);
                if (!endVal || dayjs(endVal).isBefore(date, "day")) {
                  form.setFieldValue(name[2], date);
                }
              }
              form.validateFields([
                `_datetime_range_validator_${name.join("_")}`,
              ]);
            }}
          />
        </Form.Item>
      </Col>

      <Col xs={24} sm={12}>
        <CustomTimePicker
          name={name[1]}
          label={label[1]}
          required
          form={form}
          disabled={disable}
          dateFieldName={name[0]}
          bounds={rangeBounds}
          validatePast={!isScheduleEdit}
        />
      </Col>

      {hasEnd && (
        <>
          <Col xs={24} sm={12}>
            <Form.Item
              name={name[2]!}
              label={label[2]}
              dependencies={[name[0]]}
              rules={[
                {
                  required: true,
                  message: "Please select end date",
                },
              ]}
            >
              <DatePicker
                disabled={datePickerDisabled}
                disabledDate={endDateDisabled}
                style={{ width: "100%" }}
                onChange={(date: Dayjs | null) => {
                  if (date) {
                    form.validateFields([
                      name[2]!,
                      name[3]!,
                      `_datetime_range_validator_${name.join("_")}`,
                    ]);
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <CustomTimePicker
              name={name[3]!}
              label={label[3]!}
              required
              form={form}
              disabled={disable}
              dateFieldName={name[2]!}
              fallbackDateField={name[0]}
              bounds={rangeBounds}
              minGapFrom={{
                dateField: name[0],
                timeField: name[1],
                gapMinutes: minGapMinutes,
              }}
              validatePast
            />
          </Col>
        </>
      )}
    </>
  );
};

export default DateTimePicker;
